// HRMS_STADO/src/views/hrms/offices/OfficeViewPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getOffice, getShiftsByOffice } from "services/hrms.api";
import { ChevronLeft, MapPin, Clock } from "lucide-react";
import Loading from "components/common/Loading";

export default function OfficeViewPage() {
  const { id } = useParams();
  const nav = useNavigate();

  const [office, setOffice] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shiftLoading, setShiftLoading] = useState(true);
  const [err, setErr] = useState("");
  const [shiftErr, setShiftErr] = useState("");

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setErr("");

        const { data } = await getOffice(id);
        if (!alive) return;
        setOffice(data);
      } catch (e) {
        if (!alive) return;
        setErr(e?.message || "Failed to load office.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setShiftLoading(true);
        setShiftErr("");

        const res = await getShiftsByOffice(id); // expects officeId param internally
        if (!alive) return;

        const list = res?.data || [];
        setShifts(Array.isArray(list) ? list : list?.items || []);
      } catch (e) {
        if (!alive) return;
        setShiftErr(e?.message || "Failed to load shifts.");
      } finally {
        if (alive) setShiftLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [id]);

  const coords = useMemo(() => {
    const lat = Number(office?.lat);
    const lng = Number(office?.lng);
    const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
    return { lat, lng, hasCoords };
  }, [office?.lat, office?.lng]);

  const mapSrc = useMemo(() => {
    if (!coords.hasCoords) return "";
    const { lat, lng } = coords;
    return `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01}%2C${lat - 0.01
      }%2C${lng + 0.01}%2C${lat + 0.01}&layer=mapnik&marker=${lat}%2C${lng}`;
  }, [coords]);

  const radiusText = useMemo(() => {
    const km = Number(office?.radiusKm);
    const meters = Number(office?.radiusMeters);

    if (Number.isFinite(km) && km > 0) return `${km} km`;
    if (Number.isFinite(meters) && meters > 0) return `${meters} meters`;
    return "-";
  }, [office?.radiusKm, office?.radiusMeters]);

  if (loading) return <Loading fullscreen />;

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => nav(-1)}
          className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>
        <h2 className="text-2xl font-semibold dark:text-white">
          Office Details
        </h2>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-800">
        {err ? (
          <div className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
            {err}
          </div>
        ) : !office ? (
          <div className="text-sm text-gray-600 dark:text-white/80">
            Office not found.
          </div>
        ) : (
          <div className="space-y-5">
            {/* Basic Info */}
            <div>
              <div className="text-base font-semibold dark:text-white">
                {office.name}
              </div>
              <div className="mt-1 text-sm text-gray-600 dark:text-white/80">
                {office.address}
                {office.city ? `, ${office.city}` : ""}
                {office.state ? `, ${office.state}` : ""}
                {office.country ? `, ${office.country}` : ""}
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-white/60">
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {coords.hasCoords
                    ? `${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`
                    : "No coordinates"}
                </span>

                <span>•</span>

                <span>Radius: {radiusText}</span>

                {coords.hasCoords ? (
                  <>
                    <span>•</span>
                    <a
                      href={`https://www.google.com/maps?q=${office.lat},${office.lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-brand-600 hover:underline dark:text-white"
                    >
                      Open in Google Maps
                    </a>
                  </>
                ) : null}
              </div>
            </div>

            {/* Map */}
            <div className="overflow-hidden rounded-xl border dark:border-white/10 dark:bg-navy-700">
              {coords.hasCoords ? (
                <iframe
                  title="office-map"
                  src={mapSrc}
                  className="h-56 w-full"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-56 items-center justify-center text-sm text-gray-400">
                  Map preview
                </div>
              )}
            </div>

            {/* Shifts */}
            <div className="rounded-xl border p-4 dark:border-white/10">
              <div className="mb-3 flex items-center gap-2 font-semibold dark:text-white">
                <Clock size={16} /> Shifts
              </div>

              {shiftErr ? (
                <div className="mb-3 text-sm text-rose-600 dark:text-rose-300">
                  {shiftErr}
                </div>
              ) : null}

              {shiftLoading ? (
                <div className="text-sm text-gray-600 dark:text-white/80">
                  Loading shifts…
                </div>
              ) : shifts.length === 0 ? (
                <div className="text-sm text-gray-600 dark:text-white/80">
                  No shifts configured for this office.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-gray-50 dark:bg-white/5">
                      <tr>
                        <th className="px-3 py-2 text-gray-600 dark:text-white/60">
                          Name
                        </th>
                        <th className="px-3 py-2 text-gray-600 dark:text-white/60">
                          Start
                        </th>
                        <th className="px-3 py-2 text-gray-600 dark:text-white/60">
                          End
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {shifts.map((s) => (
                        <tr
                          key={s._id}
                          className="border-t border-gray-100 dark:border-white/10"
                        >
                          <td className="px-3 py-2 dark:text-white">
                            {s.name}
                          </td>
                          <td className="px-3 py-2 dark:text-white">
                            {s.startTime}
                          </td>
                          <td className="px-3 py-2 dark:text-white">
                            {s.endTime}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => nav(`/admin/hrms/offices/${id}/edit`)}
                className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
              >
                Edit
              </button>

              <button
                onClick={() => nav(-1)}
                className="rounded-lg bg-gradient-to-r from-[#ef145d] to-[#e3955e] px-5 py-2 text-sm text-white hover:opacity-95"
              >
                Back to List
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}